class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('upsell-button')) {
  class UpsellButton extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    // Same two sections Dawn refreshes on a normal add to cart. Without the bubble
    // the header count stays stale until the next page load.
    get sectionsToRender() {
      return [
        { id: 'cart-drawer', target: '.drawer__inner', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', target: '#cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    get variantId() {
      // data-variant-id erlaubt den Einsatz außerhalb des Drawers ohne doppelte
      // id-Attribute auf der Seite. this.id bleibt als Fallback.
      return this.dataset.variantId || this.id;
    }

    async handleClick(event) {
      event.preventDefault();
      if (this.loading) return;
      await this.addToCart(this.variantId, 1);
    }

    async addToCart(variantId, quantity) {
      this.loading = true;
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: quantity,
              },
            ],
            sections: this.sectionsToRender.map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });

        const parsedState = await response.json();

        if (parsedState.status) {
          console.error('Upsell konnte nicht hinzugefügt werden:', parsedState.description || parsedState.message);
          return;
        }

        this.renderSections(parsedState.sections);

        // Auf der Produktseite ist der Drawer zu. Ohne das Öffnen bliebe der
        // Klick ohne sichtbare Rückmeldung.
        const drawer = document.querySelector('cart-drawer');
        if (drawer && !drawer.classList.contains('active')) drawer.open?.();
      } catch (error) {
        console.error('Upsell konnte nicht hinzugefügt werden:', error);
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }

    renderSections(sections) {
      if (!sections) return;

      document.querySelector('cart-drawer')?.classList.remove('is-empty');

      this.sectionsToRender.forEach((section) => {
        const target = document.querySelector(section.target);
        const html = sections[section.id];
        if (!target || !html) return;

        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (source) target.innerHTML = source.innerHTML;
      });
    }
  }

  customElements.define('upsell-button', UpsellButton);
}
